import { OrganizationRole } from 'src/context/types';
import { RoleGated } from '../types';

/**
 * Everything an action is allowed to touch, passed in explicitly. Keeping this a
 * plain object rather than reaching for hooks inside `run` is what makes the
 * registry testable without rendering anything.
 */
export interface ActionContext {
  theme: 'dark' | 'light' | 'system';
  setTheme: (theme: 'dark' | 'light' | 'system') => void;
  locale: 'vi' | 'en';
  setLocale: (locale: 'vi' | 'en') => void;
  fullscreen: boolean;
  setFullscreen: (fullscreen: boolean) => void;
  signOut: () => void;
}

export interface ActionEntry extends RoleGated {
  id: string;
  /** i18n key under `globalSearch.entries`. */
  labelKey: string;
  run: (context: ActionContext) => void;
}

const ALL: OrganizationRole[] = ['owner', 'admin', 'member'];

export const ACTION_REGISTRY: ActionEntry[] = [
  {
    id: 'toggleTheme',
    labelKey: 'toggleTheme',
    roles: ALL,
    // 'system' is treated as light so the toggle always visibly moves.
    run: (context) => context.setTheme(context.theme === 'dark' ? 'light' : 'dark'),
  },
  {
    id: 'toggleLocale',
    labelKey: 'toggleLocale',
    roles: ALL,
    run: (context) => context.setLocale(context.locale === 'vi' ? 'en' : 'vi'),
  },
  {
    id: 'toggleFullscreen',
    labelKey: 'toggleFullscreen',
    roles: ALL,
    run: (context) => context.setFullscreen(!context.fullscreen),
  },
  {
    id: 'signOut',
    labelKey: 'signOut',
    roles: ALL,
    run: (context) => context.signOut(),
  },
];

export function visibleActions(role: OrganizationRole | null, isPlatformAdmin: boolean): ActionEntry[] {
  if (isPlatformAdmin) return ACTION_REGISTRY;
  if (!role) return [];
  return ACTION_REGISTRY.filter((entry) => entry.roles.includes(role));
}
